/** Dev-only static preview of a calmer Scanner shortlist.
 *
 * Visual alignment with the Ticker Intelligence Workspace only.
 * Does not change production Scanner filters, cache, refresh, or routes.
 * Open Workspace stays on the workspace preview — never /ticker/:ticker.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { StatusBadge } from "@/components/wheel/StatusBadge";
import {
  EarningsQualityBadge, MockBadge, OptionStateBadge, ProvenanceTable,
  ScoreBar, WorkspaceAdminProvider, WorkspacePanel,
} from "@/workspace/primitives";
import { SCANNER_REFRESH_PROVENANCE } from "@/workspace/fieldProvenance";
import { SCANNER_FILTERS, SCANNER_REFRESH_ROWS, SCANNER_WATCHLISTS } from "@/workspace/mockScannerRefresh";
import { HELPER, INCOME_FILTERS, SCREEN } from "@/workspace/screenLanguage";

const INCOME = {
  clear: { tier: "info", compact: SCREEN.clear.compact, full: SCREEN.clear.full },
  flag: { tier: "caution", compact: SCREEN.flag.compact, full: SCREEN.flag.full },
  unavailable: { tier: "blocking", compact: SCREEN.unavailable.compact, full: SCREEN.unavailable.full },
  preparing: { tier: "info", compact: SCREEN.preparing.compact, full: SCREEN.preparing.full },
};

const OPTIONS = {
  observed: "observed",
  partial: "partial",
  unverified: "unverified",
  preparing: null,
  unavailable: "unavailable",
};

function FilterSelect({ label, value, options, onChange, testid }) {
  return (
    <label className="flex items-center gap-1.5 text-[10px] shrink-0">
      <span style={{ color: "var(--hoip-text-tertiary)" }}>{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="text-[11px] px-1.5 py-1 rounded-sm border bg-transparent"
        style={{
          color: "var(--hoip-text-secondary)",
          borderColor: "var(--hoip-border)",
          background: "var(--hoip-card)",
        }}
      >
        {options.map((o) => (
          <option key={o} value={o}>{o === "all" ? "All" : o}</option>
        ))}
      </select>
    </label>
  );
}

function Toggle({ label, active, onClick, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="text-[11px] px-2 py-0.5 rounded-sm border font-mono-data"
      style={{
        color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
        background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
        borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
      }}
      data-testid={testid}
    >
      {label}
    </button>
  );
}

export default function ScannerRefreshPreviewPage() {
  const [adminView, setAdminView] = useState(false);
  const [q, setQ] = useState("");
  const [regime, setRegime] = useState("all");
  const [income, setIncome] = useState("all");
  const [earnings, setEarnings] = useState("all");
  const [options, setOptions] = useState("all");
  const [listId, setListId] = useState("my");
  const [preparing, setPreparing] = useState({});
  const [incomeTip, setIncomeTip] = useState(null);
  const activeList = SCANNER_WATCHLISTS.find((w) => w.id === listId) || SCANNER_WATCHLISTS[0];
  const listSet = new Set(activeList.tickers);

  const rows = useMemo(() => SCANNER_REFRESH_ROWS.filter((r) => {
    if (!listSet.has(r.ticker)) return false;
    if (q && !`${r.ticker} ${r.name}`.toLowerCase().includes(q.toLowerCase())) return false;
    if (regime !== "all" && r.confirmedRegime !== regime) return false;
    if (income !== "all" && r.incomeContext !== income) return false;
    if (earnings !== "all" && r.earnings.quality !== earnings) return false;
    if (options !== "all" && r.optionsState !== options) return false;
    return true;
  }), [q, regime, income, earnings, options, listId]);

  return (
    <WorkspaceAdminProvider value={adminView}>
      <div
        className="min-h-screen p-3 md:p-6 max-w-[1400px] mx-auto space-y-3 overflow-x-hidden"
        style={{ background: "var(--hoip-bg)", color: "var(--hoip-text-primary)" }}
        data-testid="scanner-refresh-preview"
      >
        <div
          className="border p-2.5 flex flex-wrap items-center gap-x-4 gap-y-2"
          style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }}
        >
          <span className="text-[11px] font-bold uppercase tracking-wide" style={{ fontFamily: "Chivo, sans-serif" }}>
            Dev preview · Scanner visual refresh
          </span>
          <span className="flex items-center gap-1.5">
            <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>View as</span>
            <Toggle label="User" active={!adminView} onClick={() => setAdminView(false)} testid="preview-view-user" />
            <Toggle label="Admin" active={adminView} onClick={() => setAdminView(true)} testid="preview-view-admin" />
          </span>
          <span className="text-[10px]" style={{ color: "var(--hoip-text-tertiary)" }}>
            Static shortlist. Production Scanner is unchanged. Open Workspace stays on the workspace preview.
          </span>
        </div>

        <WorkspacePanel
          title="Watchlists"
          subtitle="Personal, portfolio and sector lists · mock membership only"
          notice={<MockBadge />}
          hint="Does not change production Scanner watchlist logic. My Watchlist is capped at 20 tickers in the product model."
          testid="scanner-watchlists"
        >
          <div className="space-y-1.5">
            <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Personal</div>
            <div className="flex gap-1.5 overflow-x-auto min-w-0 pb-1" data-testid="watchlist-personal-row">
              {SCANNER_WATCHLISTS.filter((w) => w.group !== "sector").map((w) => {
                const active = listId === w.id;
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setListId(w.id)}
                    data-testid={`watchlist-${w.id}`}
                    className="shrink-0 text-[10px] px-2 py-1 rounded-sm border"
                    style={{
                      color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                      background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                      borderColor: active ? "var(--hoip-text-primary)" : w.group === "portfolio" ? "#93C5FD" : "var(--hoip-border)",
                    }}
                  >
                    {w.label}
                    <span className="ml-1 font-mono-data" style={{ opacity: 0.7 }}>
                      {w.id === "my" ? `${w.tickers.length}/20` : w.tickers.length}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="text-[9px] uppercase tracking-wide" style={{ color: "var(--hoip-text-tertiary)" }}>Sectors</div>
            <div className="flex gap-1.5 overflow-x-auto min-w-0 pb-1" data-testid="watchlist-sector-row">
              {SCANNER_WATCHLISTS.filter((w) => w.group === "sector").map((w) => {
                const active = listId === w.id;
                return (
                  <button
                    key={w.id}
                    type="button"
                    onClick={() => setListId(w.id)}
                    data-testid={`watchlist-${w.id}`}
                    className="shrink-0 text-[10px] px-2 py-1 rounded-sm border"
                    style={{
                      color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                      background: active ? "var(--hoip-text-primary)" : "var(--hoip-bg)",
                      borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                      borderStyle: "dashed",
                    }}
                  >
                    {w.label}
                    <span className="ml-1 font-mono-data" style={{ opacity: 0.7 }}>{w.tickers.length}</span>
                  </button>
                );
              })}
            </div>
            <p className="text-[9px] sm:hidden" style={{ color: "var(--hoip-text-tertiary)" }}>
              Scroll sideways for the remaining sector lists.
            </p>
          </div>
        </WorkspacePanel>

        <WorkspacePanel
          title="Shortlist"
          subtitle="Fast route into the Ticker Intelligence Workspace"
          notice={<MockBadge />}
          hint="Visual alignment only. Filters here do not change production Scanner logic, cache or prioritisation."
          testid="scanner-refresh-filters"
        >
          <div className="flex flex-wrap gap-2 items-center min-w-0" data-testid="scanner-filter-row">
            <label className="flex items-center gap-1.5 text-[10px] min-w-0">
              <span style={{ color: "var(--hoip-text-tertiary)" }}>Ticker</span>
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Filter"
                data-testid="scanner-filter-q"
                className="text-[11px] px-1.5 py-1 rounded-sm border w-28"
                style={{
                  color: "var(--hoip-text-secondary)",
                  borderColor: "var(--hoip-border)",
                  background: "var(--hoip-card)",
                }}
              />
            </label>
            <FilterSelect label="Regime" value={regime} options={SCANNER_FILTERS.regimes} onChange={setRegime} testid="scanner-filter-regime" />
            <div className="flex items-center gap-1.5 min-w-0" data-testid="scanner-filter-income">
              <span className="text-[10px] shrink-0" style={{ color: "var(--hoip-text-tertiary)" }}>Income</span>
              <div className="flex gap-1 overflow-x-auto min-w-0">
                {INCOME_FILTERS.map((o) => {
                  const active = income === o.value;
                  return (
                    <div key={o.value} className="relative shrink-0">
                      <button
                        type="button"
                        onClick={() => setIncome(o.value)}
                        title={o.full}
                        aria-label={o.full}
                        onMouseEnter={() => setIncomeTip(o.full)}
                        onMouseLeave={() => setIncomeTip(null)}
                        onFocus={() => setIncomeTip(o.full)}
                        onBlur={() => setIncomeTip(null)}
                        data-testid={`scanner-income-${o.value}`}
                        className="peer shrink-0 text-[10px] px-2 py-1 rounded-sm border"
                        style={{
                          color: active ? "var(--hoip-bg)" : "var(--hoip-text-secondary)",
                          background: active ? "var(--hoip-text-primary)" : "var(--hoip-card)",
                          borderColor: active ? "var(--hoip-text-primary)" : "var(--hoip-border)",
                        }}
                      >
                        {o.compact}
                      </button>
                      <span
                        role="tooltip"
                        data-testid={`scanner-income-tip-${o.value}`}
                        className="absolute left-0 top-full z-20 mt-1 hidden whitespace-nowrap rounded-sm border px-1.5 py-1 text-[10px] peer-hover:block peer-focus:block peer-focus-visible:block"
                        style={{
                          background: "var(--hoip-card)",
                          borderColor: "var(--hoip-border)",
                          color: "var(--hoip-text-primary)",
                        }}
                      >
                        {o.full}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
            <FilterSelect label="Earnings" value={earnings} options={SCANNER_FILTERS.earnings} onChange={setEarnings} testid="scanner-filter-earnings" />
            <FilterSelect label="Options" value={options} options={SCANNER_FILTERS.options} onChange={setOptions} testid="scanner-filter-options" />
          </div>
          <p
            className="text-[10px] mt-2 leading-snug"
            style={{ color: "var(--hoip-text-secondary)" }}
            data-testid="scanner-income-live-tip"
          >
            {incomeTip || "Hover or focus an Income option for the full label."}
          </p>
          <p className="text-[10px] mt-2 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="scanner-screen-helper">
            {HELPER.screenFilter}
          </p>
          <p className="text-[10px] mt-1 leading-snug" style={{ color: "var(--hoip-text-tertiary)" }} data-testid="scanner-colour-helper">
            {HELPER.colour}
          </p>
        </WorkspacePanel>

        <WorkspacePanel title="Universe" subtitle={`${activeList.label} · ${rows.length} mock rows`} testid="scanner-refresh-table">
          <div className="overflow-x-auto min-w-0" data-testid="scanner-table-scroll">
            <table className="w-full text-[11px] min-w-[52rem]">
              <thead>
                <tr className="text-left uppercase tracking-wide text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  <th className="py-1 pr-3 font-medium">Ticker</th>
                  <th className="py-1 pr-3 font-medium">Technical</th>
                  <th className="py-1 pr-3 font-medium">Fundamental</th>
                  <th className="py-1 pr-3 font-medium">Confirmed HMM</th>
                  <th className="py-1 pr-3 font-medium">Next earnings</th>
                  <th className="py-1 pr-3 font-medium">Option data</th>
                  <th className="py-1 pr-3 font-medium">Income</th>
                  <th className="py-1 pr-3 font-medium">Cause</th>
                  <th className="py-1 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const inc = INCOME[r.incomeContext] || INCOME.preparing;
                  const optKey = OPTIONS[r.optionsState];
                  const href = r.workspaceTicker
                    ? `/dev/ticker-workspace-preview?ticker=${r.ticker}`
                    : "/dev/ticker-workspace-preview";
                  return (
                    <tr
                      key={r.ticker}
                      className="border-t align-top"
                      style={{ borderColor: "var(--hoip-divider)" }}
                      data-testid={`scanner-row-${r.ticker}`}
                    >
                      <td className="py-2 pr-3">
                        <div className="font-mono-data font-bold">{r.ticker}</div>
                        <div className="text-[9px] truncate max-w-[10rem]" style={{ color: "var(--hoip-text-tertiary)" }}>
                          {r.name}
                        </div>
                      </td>
                      <td className="py-2 pr-3 min-w-[7rem]">
                        {r.technicalScore == null ? (
                          <span style={{ color: "var(--hoip-text-tertiary)" }}>{r.technicalState}</span>
                        ) : (
                          <>
                            <ScoreBar value={r.technicalScore} />
                            <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{r.technicalState}</div>
                          </>
                        )}
                      </td>
                      <td className="py-2 pr-3 min-w-[7rem]">
                        {r.fundamentalScore == null ? (
                          <span style={{ color: "var(--hoip-text-tertiary)" }}>{r.fundamentalState}</span>
                        ) : (
                          <>
                            <ScoreBar value={r.fundamentalScore} />
                            <div className="text-[9px] mt-0.5" style={{ color: "var(--hoip-text-tertiary)" }}>{r.fundamentalState}</div>
                          </>
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <div className="font-mono-data">{r.confirmedRegime || "—"}</div>
                        <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                          {r.confidence == null ? "Preparing" : `${Math.round(r.confidence * 100)}% confidence`}
                        </div>
                      </td>
                      <td className="py-2 pr-3">
                        <div className="font-mono-data">{r.earnings.date || "—"}</div>
                        <div className="mt-0.5"><EarningsQualityBadge quality={r.earnings.quality} /></div>
                      </td>
                      <td className="py-2 pr-3">
                        {optKey ? (
                          <OptionStateBadge state={optKey} />
                        ) : (
                          <StatusBadge tier="info" text="Preparing" title="Bounded refresh concept — visual only" />
                        )}
                      </td>
                      <td className="py-2 pr-3">
                        <StatusBadge tier={inc.tier} text={inc.compact} title={inc.full} />
                      </td>
                      <td className="py-2 pr-3 text-[10px]" style={{ color: "var(--hoip-text-secondary)" }}>
                        {(r.incomeContext === "flag" || r.incomeContext === "unavailable") ? (r.cause || "—") : "—"}
                      </td>
                      <td className="py-2">
                        <div className="flex flex-col gap-1 items-start">
                          <Link
                            to={href}
                            className="text-[10px] px-2 py-1 rounded-sm border"
                            style={{
                              color: "var(--hoip-bg)",
                              background: "var(--hoip-text-primary)",
                              borderColor: "var(--hoip-text-primary)",
                            }}
                            data-testid={`scanner-open-${r.ticker}`}
                          >
                            Open Workspace
                          </Link>
                          <button
                            type="button"
                            onClick={() => setPreparing((p) => ({ ...p, [r.ticker]: true }))}
                            className="text-[10px] px-2 py-1 rounded-sm border"
                            style={{
                              color: "var(--hoip-text-secondary)",
                              borderColor: "var(--hoip-border)",
                              background: "var(--hoip-card)",
                            }}
                            data-testid={`scanner-prepare-${r.ticker}`}
                          >
                            {preparing[r.ticker] ? "Preparing…" : "Prepare data"}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </WorkspacePanel>

        <WorkspacePanel
          title="Field provenance"
          subtitle="Scanner preview fixtures only"
          testid="scanner-refresh-provenance"
        >
          <ProvenanceTable rows={SCANNER_REFRESH_PROVENANCE} testid="scanner-refresh-provenance-table" />
        </WorkspacePanel>
      </div>
    </WorkspaceAdminProvider>
  );
}
